import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Badge, Button, Card, PageHeader } from '@/components/ui/primitives';
import { EmptyState, ErrorState, LoadingState } from '@/components/DataState';
import { fetchSeoProjectOptions, fetchSeoRankTracking } from '@/data/sources';
import { formatRelative } from '@/lib/utils';
import { useActiveDomain } from '@/hooks/useActiveDomain';
import { resolveActiveProjectId } from '@/pages/seo/domain-scope';
import { DomainChoiceGate } from '@/pages/seo/DomainChoiceGate';

/* /seo/rank-tracking — Rank Tracking (W1a). Read-only view over the stored
 * bc_seo_rankings observations (scheduler-fed, already live).
 *
 * HONESTY + SAFETY INVARIANTS (W1a contract):
 *  - FREE + read-only: reads stored rankings, never a provider call, never a
 *    charge. Safe to AUTO-LOAD on mount (unlike the paid Domain Overview /
 *    Organic Keywords surfaces, which are user-triggered).
 *  - Source-attributed ("bc_seo_rankings"); every null position/delta renders
 *    as an em-dash — numbers are NEVER invented (position null = checked but
 *    not found within depth).
 *  - Honest states: loading / error / empty, via the shared DataState system.
 */

const SOURCE_TAG = 'Source: Stored rank tracking (bc_seo_rankings)';

function formatInt(n: number): string {
  return n.toLocaleString('en-GB');
}

/** Render a rank position honestly: em-dash for null (checked, not found). */
function positionCell(v: number | null): string {
  return v === null ? '—' : formatInt(v);
}

/** Directional delta glyph — the API sends (previous − current) position, so POSITIVE = improved (moved up the SERP). */
function deltaLabel(delta: number | null): { text: string; tone: 'up' | 'down' | 'flat' | 'na' } {
  if (delta === null) return { text: '—', tone: 'na' };
  if (delta > 0) return { text: `▲ ${delta}`, tone: 'up' };
  if (delta < 0) return { text: `▼ ${Math.abs(delta)}`, tone: 'down' };
  return { text: '– 0', tone: 'flat' };
}

export function SeoRankTrackingPage() {
  const navigate = useNavigate();

  const projectsQ = useQuery({
    queryKey: ['seo', 'project-options'],
    queryFn: fetchSeoProjectOptions,
  });
  const projects = projectsQ.data ?? [];
  const { active, select } = useActiveDomain();

  const projectId = resolveActiveProjectId(active, projects);
  const project = projects.find((p) => p.id === projectId) ?? null;

  // FREE read-only surface — auto-loads for the selected project. No provider
  // call, no charge, so wiring it to a query on mount is safe and correct.
  const rankQ = useQuery({
    queryKey: ['seo', 'rank-tracking', projectId],
    queryFn: () => fetchSeoRankTracking(projectId as string),
    enabled: projectId !== null,
  });

  if (projectsQ.isPending) {
    return (
      <div className="space-y-6">
        <PageHeader title="Rank Tracking" />
        <LoadingState rows={4} />
      </div>
    );
  }
  if (projectsQ.error) {
    return (
      <div className="space-y-6">
        <PageHeader title="Rank Tracking" />
        <ErrorState error={projectsQ.error} onRetry={() => void projectsQ.refetch()} />
      </div>
    );
  }
  if (projects.length > 0 && projectId === null) {
    return <DomainChoiceGate options={projects} onSelect={(id) => void select(Number(id))} />;
  }
  if (projects.length === 0) {
    return (
      <div className="space-y-6">
        <PageHeader title="Rank Tracking" />
        <EmptyState
          title="No project to track yet"
          teach="Rank Tracking reads the stored ranking history for an SEO project. Add your first domain on the Projects page, then rankings appear here as the scheduler records them."
          action={
            <Button variant="primary" onClick={() => navigate('/seo/projects')}>
              Go to Projects
            </Button>
          }
        />
      </div>
    );
  }

  const rankings = rankQ.data?.rankings ?? [];
  const source = rankQ.data?.source ?? 'bc_seo_rankings';
  // Freshest observation across the tracked keywords, for the "last checked" line.
  const lastCheckedAt = rankings.reduce<string | null>((acc, r) => {
    if (!r.checked_at) return acc;
    return acc === null || r.checked_at > acc ? r.checked_at : acc;
  }, null);

  const projectSelector = (
    <label className="flex items-center gap-2">
      <span className="text-xs font-medium text-text-secondary">Domain</span>
      <select
        value={projectId ?? ''}
        onChange={(e) => void select(Number(e.target.value))}
        className="h-control rounded-md border border-border-default bg-canvas px-3 text-sm text-text-primary outline-none focus:border-ai focus:ring-2 focus:ring-ai/30"
        aria-label="SEO project"
      >
        {projects.map((p) => (
          <option key={p.id} value={p.id}>
            {p.label}
          </option>
        ))}
      </select>
    </label>
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Rank Tracking"
        subtitle="Stored ranking history for your tracked keywords — read straight from the scheduler-fed record, source-attributed, and free to view."
        actions={projectSelector}
      />

      <Card className="p-4">
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone="neutral">{SOURCE_TAG}</Badge>
          <span className="text-xs text-text-tertiary">
            {lastCheckedAt
              ? `Last checked ${formatRelative(lastCheckedAt)}`
              : 'No ranking observations recorded yet for this project'}
          </span>
        </div>

        <div className="mt-4">
          {rankQ.isPending ? (
            <LoadingState rows={4} />
          ) : rankQ.error ? (
            <ErrorState error={rankQ.error} onRetry={() => void rankQ.refetch()} />
          ) : rankings.length === 0 ? (
            <EmptyState
              title="No rankings stored yet"
              teach={`No ranking observations have been recorded for ${project?.domain ?? 'this project'} yet. As the scheduler checks your tracked keywords, their positions appear here — nothing is invented in the meantime.`}
            />
          ) : (
            <div className="overflow-hidden rounded-lg border border-border-subtle bg-surface">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[680px] border-collapse">
                  <thead>
                    <tr className="border-b border-border-subtle bg-inset/50 text-left text-[11px] uppercase tracking-wide text-text-tertiary">
                      <th className="px-3 py-2 font-semibold">Keyword</th>
                      <th className="px-3 py-2 text-right font-semibold">Position</th>
                      <th className="px-3 py-2 text-right font-semibold">Previous</th>
                      <th className="px-3 py-2 text-right font-semibold">Change</th>
                      <th className="px-3 py-2 font-semibold">Last checked</th>
                      <th className="px-3 py-2 font-semibold">Source</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rankings.map((r, i) => {
                      const d = deltaLabel(r.delta);
                      const deltaColor =
                        d.tone === 'up'
                          ? 'text-success'
                          : d.tone === 'down'
                            ? 'text-danger'
                            : 'text-text-tertiary';
                      return (
                        <tr
                          key={`${r.keyword}-${i}`}
                          className="border-b border-border-subtle last:border-0 hover:bg-hover"
                        >
                          <td className="px-3 py-2.5 text-sm font-medium text-text-primary">
                            {r.keyword}
                          </td>
                          <td className="px-3 py-2.5 text-right font-mono text-sm text-text-primary" data-tabular>
                            {positionCell(r.position)}
                          </td>
                          <td className="px-3 py-2.5 text-right font-mono text-sm text-text-secondary" data-tabular>
                            {positionCell(r.previous_position)}
                          </td>
                          <td className={`px-3 py-2.5 text-right font-mono text-sm ${deltaColor}`} data-tabular>
                            {d.text}
                          </td>
                          <td className="px-3 py-2.5 text-sm text-text-secondary">
                            {r.checked_at ? formatRelative(r.checked_at) : '—'}
                          </td>
                          <td className="px-3 py-2.5 text-sm text-text-tertiary">{r.source}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="border-t border-border-subtle px-3 py-2 text-[11px] text-text-tertiary">
                {formatInt(rankings.length)} tracked{' '}
                {rankings.length === 1 ? 'keyword' : 'keywords'} · read-only from{' '}
                {source} · a null position means the keyword was checked but not found within the
                checked depth — shown honestly, never as zero.
              </div>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
